import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse, AxiosResponseHeaders } from "axios";
import { Thunk } from "../../store";
import {
  BackendObtenerArchivo,
  getCurrentDomain,
} from "../../../apis/backend/Api";
import mock_Json from "../../../dataJson/obtenerArchivoAdjunto.json";
import { formatearMensajeBackend } from "../../../apis/helper/helperApi";
import { TypeStatus } from "../../helpers/constantes";

interface State {
  obtenerArchivo: any[];
  base64: string;
  tipo: string;
  status: TypeStatus;
  isLoading: boolean;
  Success: boolean;
  Mensaje: string | null;
  error: string | null;
}

const initialState: State = {
  obtenerArchivo: [],
  base64: "",
  tipo: "",
  status: "idle",
  isLoading: false,
  Success: false,
  Mensaje: "",
  error: null,
};

interface defaultResponse {
  data: any;
  status: number;
  statusText: string;
  headers: AxiosResponseHeaders | {};
  config: any;
  request?: any;
}
const defaultResp: defaultResponse = {
  data: undefined,
  status: 0,
  statusText: "",
  headers: {},
  config: undefined,
};

interface ParsedDataUrl {
  mimeType: string; // ej: 'image/png'
  extension: string | null; // ej: 'png' (derivado del mimeType)
  base64Data: string; // ej: 'iVBORw0KGgoAAAANSUhEUgAA....=='
}
function parseDataUrl(dataUrl: string): ParsedDataUrl | null {
  // Expresión Regular para capturar las partes:
  // 1. ^data:      - Debe empezar con 'data:'
  // 2. ([^;]+)     - Grupo 1: Captura cualquier carácter excepto ';' (este es el MIME type)
  // 3. ;base64,    - Debe seguir con ';base64,' (asumimos que siempre es base64)
  // 4. (.*)        - Grupo 2: Captura el resto de la cadena (los datos base64)
  // 5. $           - Debe terminar ahí
  const regex = /^data:([^;]+);base64,(.*)$/;
  const match = dataUrl.match(regex);

  if (match && match[1] && match[2]) {
    const mimeType = match[1];
    const base64Data = match[2];

    // Intentar derivar la extensión del MIME type
    // Usualmente es la parte después de la barra '/'
    let extension: string | null = null;
    const mimeParts = mimeType.split("/");
    if (mimeParts.length === 2) {
      extension = mimeParts[1];
      // Podrías querer normalizar algunas extensiones si es necesario
      // ej: 'jpeg' -> 'jpg', 'svg+xml' -> 'svg'
      if (extension === "jpeg") extension = "jpg";
      if (extension.includes("+")) {
        // Como 'svg+xml'
        extension = extension.split("+")[0];
      }
    }

    return {
      mimeType,
      extension,
      base64Data,
    };
  }

  // Si el formato no coincide, devolvemos null
  return null;
}

const obtenerArchivoAdjuntoSlice = createSlice({
  name: "obtenerArchivo",
  initialState,
  reducers: {
    setObtenerArchivo: (state, action: PayloadAction<any[]>) => {
      state.obtenerArchivo = action.payload;
    },

    setBase64: (state, action: PayloadAction<string>) => {
      state.base64 = action.payload;
    },

    setTipo: (state, action: PayloadAction<string>) => {
      state.tipo = action.payload;
    },
    getObtenerArchivo: (state, action: PayloadAction<any[]>) => {
      state.obtenerArchivo = action.payload;
    },
    setClearObtenerArchivo: (state, action: PayloadAction<any[]>) => {
      state = initialState;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setStatus: (state, action: PayloadAction<TypeStatus>) => {
      state.status = action.payload;
    },
    setResult: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSuccess: (state, action: PayloadAction<boolean>) => {
      state.Success = action.payload;
    },
    setMensaje: (state, action: PayloadAction<string>) => {
      state.Mensaje = action.payload;
    },
  },
});

export const setObtenerArchivoAdjunto =
  (Caso: number, IdArchivo: number): Thunk =>
  async (dispatch): Promise<AxiosResponse | AxiosError> => {
    dispatch(setIsLoading(true));
    try {
      const response: AxiosResponse = await BackendObtenerArchivo(
        Caso,
        IdArchivo,
      );

      const isError =
        response.data?.Success === false ||
        (response.data?.ResultadoSolicitud !== undefined &&
          response.data?.ResultadoSolicitud !== 200);

      if (isError) {
        dispatch(setIsLoading(false));
        dispatch(setStatus("failed"));
        dispatch(setSuccess(false));
      } else {
        dispatch(setObtenerArchivo(response.data.Data));
        const parsedArchivo = parseDataUrl(response.data.Data?.[0] || "");
        dispatch(
          setBase64(parsedArchivo?.base64Data ? parsedArchivo.base64Data : ""),
        );
        dispatch(
          setTipo(parsedArchivo?.extension ? parsedArchivo.extension : ""),
        );
        dispatch(setIsLoading(false));
        dispatch(setStatus("succeeded"));
        dispatch(setSuccess(true));
      }
      dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));

      return response.data;
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(setSuccess(false));
      dispatch(setStatus("failed"));

      if (error.response?.status === 401) {
        dispatch(setStatus("noAutorice"));
        dispatch(
          setMensaje(
            formatearMensajeBackend(
              "Tu sesión ha expirado. Vuelve a iniciar sesión",
            ),
          ),
        );
      } else if (error.response) {
        // Hay respuesta del servidor, pero es otro error (400, 422, 500, etc.)
        console.error(
          "Error de servidor:",
          error.response.status,
          error.response.data,
        );

        const errorData = error.response.data;
        const mensajeBackend = errorData?.Mensaje;
        const resultadoSolicitud = errorData?.ResultadoSolicitud;

        if (
          mensajeBackend &&
          (resultadoSolicitud !== 200 || errorData?.Success === false)
        ) {
          dispatch(setMensaje(formatearMensajeBackend(mensajeBackend)));
        } else {
          dispatch(
            setMensaje(
              `Error ${error.response.status}: Ocurrió un problema en el servidor.`,
            ),
          );
        }
      } else if (error.request) {
        console.error("Error de conexión:", error.message);
        dispatch(
          setMensaje(
            "No se pudo conectar al servidor. Por favor, revisa tu conexión a internet o tu VPN.",
          ),
        );
      } else {
        console.error("Error de red o solicitud:", error.message);
        dispatch(
          setMensaje(
            "Ocurrió un problema con la red. Por favor, verifica tu conexión.",
          ),
        );
      }
      return error as AxiosError;
    } finally {
      dispatch(setIsLoading(false));
      if (getCurrentDomain() === "localhost") {
        dispatch(setIsLoading(true));
        let response: AxiosResponse = defaultResp;
        response.data = mock_Json;

        const mockData: any = mock_Json;
        const isErrorMock =
          mockData?.Success === false ||
          (mockData?.ResultadoSolicitud !== undefined &&
            mockData?.ResultadoSolicitud !== 200);

        if (isErrorMock) {
          dispatch(setIsLoading(false));
          dispatch(setStatus("failed"));
          dispatch(setSuccess(false));
        } else {
          dispatch(setObtenerArchivo(response.data.Data));
          const parsedArchivo = parseDataUrl(response.data.Data?.[0] || "");
          dispatch(
            setBase64(parsedArchivo?.base64Data ? parsedArchivo.base64Data : ""),
          );
          dispatch(
            setTipo(parsedArchivo?.extension ? parsedArchivo.extension : ""),
          );
          dispatch(setIsLoading(false));
          dispatch(setStatus("succeeded"));
          dispatch(setSuccess(true));
        }
        dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));
        const mock401 = await import("../../../dataJson/401.json");
        if (mock401.status === 401) {
          dispatch(setStatus("noAutorice"));
          dispatch(
            setMensaje(
              formatearMensajeBackend(
                "Tu sesión ha expirado. Vuelve a iniciar sesión",
              ),
            ),
          );
        }
      }
    }
  };

export const setClearObtenerArchivoAdjunto =
  (): Thunk =>
  async (dispatch): Promise<AxiosResponse | AxiosError> => {
    dispatch(setIsLoading(true));
    try {
      let response: AxiosResponse = defaultResp;
      dispatch(setObtenerArchivo([]));
      dispatch(setBase64(""));
      dispatch(setTipo(""));
      dispatch(setIsLoading(false));
      dispatch(setStatus("idle"));
      dispatch(setSuccess(false));
      dispatch(setMensaje(""));
      return response.data;
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(setStatus("failed"));
      return error as AxiosError;
    } finally {
      dispatch(setIsLoading(false));
      dispatch(setObtenerArchivo([]));
      dispatch(setBase64(""));
      dispatch(setTipo(""));
      dispatch(setIsLoading(false));
      dispatch(setStatus("idle"));
      dispatch(setSuccess(false));
      dispatch(setMensaje(""));
    }
  };

export const {
  setObtenerArchivo,
  setBase64,
  setTipo,
  getObtenerArchivo,
  setClearObtenerArchivo,
  setIsLoading,
  setStatus,
  setResult,
  setSuccess,
  setMensaje,
} = obtenerArchivoAdjuntoSlice.actions;

export default obtenerArchivoAdjuntoSlice.reducer;
